import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Moon, Sun, UserPlus, Check, X, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyScope } from '../context/CompanyScopeContext';

export default function ShiftManagement() {
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: '',
    shift_type: 'night',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    reason: ''
  });

  useEffect(() => { fetchShifts(); fetchEmployees(); }, []);

  const fetchShifts = async () => {
    try {
      const res = await api.get('/shifts', { params: { status: null } });
      setShifts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/employees');
      setEmployees(res.data);
    } catch (e) { console.error(e); }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, end_date: form.end_date || null };
      await api.post('/shifts/assign', payload);
      toast.success('Shift assigne avec succes');
      setAssignOpen(false);
      setForm({ employee_id: '', shift_type: 'night', start_date: new Date().toISOString().split('T')[0], end_date: '', reason: '' });
      fetchShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(`/shifts/${id}/approve`);
      toast.success('Demande approuvee');
      fetchShifts();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/shifts/${id}/reject`);
      toast.success('Demande refusee');
      fetchShifts();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Annuler ce shift ?')) return;
    try {
      await api.delete(`/shifts/${id}`);
      toast.success('Shift annule');
      fetchShifts();
    } catch (e) { toast.error('Erreur'); }
  };

  const getStatusBadge = (status) => {
    const map = {
      active: { label: 'Actif', cls: 'bg-green-100 text-green-700 border-green-200' },
      pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
      rejected: { label: 'Refuse', cls: 'bg-red-100 text-red-700 border-red-200' },
      cancelled: { label: 'Annule', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
      replaced: { label: 'Remplace', cls: 'bg-slate-100 text-slate-500 border-slate-200' }
    };
    const v = map[status] || map.active;
    return <Badge variant="outline" className={v.cls}>{v.label}</Badge>;
  };

  const getShiftIcon = (type) => type === 'night' ? <Moon className="w-4 h-4 text-indigo-500" /> : <Sun className="w-4 h-4 text-amber-500" />;

  // Filtre par entreprise sélectionnée
  const scopedShifts = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return shifts;
    return shifts.filter(s => s.company_id === selectedCompanyId);
  }, [shifts, selectedCompanyId, isCrossCompany]);

  const scopedEmployees = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return employees;
    return employees.filter(e => e.company_id === selectedCompanyId);
  }, [employees, selectedCompanyId, isCrossCompany]);

  const pendingShifts = scopedShifts.filter(s => s.status === 'pending');
  const activeShifts = scopedShifts.filter(s => s.status === 'active');
  const otherShifts = scopedShifts.filter(s => !['pending', 'active'].includes(s.status));

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="shift-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Gestion des Shifts</h1>
          <p className="text-slate-600 mt-1">Assignation et demandes de shift jour/nuit</p>
        </div>
        <Button onClick={() => setAssignOpen(true)} data-testid="assign-shift-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Assigner un shift
        </Button>
      </div>

      {/* Pending Requests */}
      {pendingShifts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="text-amber-700 flex items-center gap-2">
              <Clock className="w-5 h-5" /> Demandes en attente ({pendingShifts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingShifts.map(s => (
              <div key={s.id} className="flex items-center justify-between p-3 bg-white rounded-lg border" data-testid={`pending-${s.id}`}>
                <div className="flex items-center gap-3">
                  {getShiftIcon(s.shift_type)}
                  <div>
                    <p className="font-medium">{s.employee_name}</p>
                    <p className="text-sm text-slate-500">
                      Shift {s.shift_type === 'night' ? 'nuit' : 'jour'} - Du {s.start_date} {s.end_date ? `au ${s.end_date}` : '(permanent)'}
                    </p>
                    {s.reason && <p className="text-xs text-slate-400 mt-1">Motif: {s.reason}</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleApprove(s.id)} data-testid={`approve-${s.id}`}>
                    <Check className="w-3 h-3 mr-1" /> Approuver
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleReject(s.id)} data-testid={`reject-${s.id}`}>
                    <X className="w-3 h-3 mr-1" /> Refuser
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Active Shifts */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Moon className="w-5 h-5 text-indigo-500" /> Shifts actifs ({activeShifts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeShifts.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Aucun shift actif. Tous les employes sont en horaire jour.</p>
          ) : (
            <div className="space-y-2">
              {activeShifts.map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border" data-testid={`active-${s.id}`}>
                  <div className="flex items-center gap-3">
                    {getShiftIcon(s.shift_type)}
                    <div>
                      <p className="font-medium">{s.employee_name}</p>
                      <p className="text-sm text-slate-500">
                        Du {s.start_date} {s.end_date ? `au ${s.end_date}` : '(permanent)'} - {s.assignment_type === 'admin' ? 'Assigne' : 'Demande approuvee'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(s.status)}
                    <Button size="sm" variant="ghost" onClick={() => handleCancel(s.id)} data-testid={`cancel-${s.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* History */}
      {otherShifts.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-slate-500">Historique</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {otherShifts.slice(0, 20).map(s => (
                <div key={s.id} className="flex items-center justify-between p-2 rounded opacity-60">
                  <div className="flex items-center gap-2">
                    {getShiftIcon(s.shift_type)}
                    <span className="text-sm">{s.employee_name} - {s.start_date}</span>
                  </div>
                  {getStatusBadge(s.status)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assign Modal */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assigner un shift</DialogTitle></DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4">
            <div className="space-y-2">
              <Label>Employe *</Label>
              <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="w-full rounded-md border border-slate-300 p-2" required data-testid="assign-employee-select">
                <option value="">-- Choisir un employe --</option>
                {scopedEmployees.filter(e => e.status === 'active').map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} ({emp.matricule})</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Type de shift *</Label>
              <select value={form.shift_type} onChange={(e) => setForm({ ...form, shift_type: e.target.value })} className="w-full rounded-md border border-slate-300 p-2" data-testid="assign-shift-type">
                <option value="night">Nuit (19h00 - 05h00)</option>
                <option value="day">Jour (08h30 - 17h30)</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date debut *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required data-testid="assign-start-date" />
              </div>
              <div className="space-y-2">
                <Label>Date fin</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="assign-end-date" />
                <p className="text-xs text-slate-500">Vide = permanent</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Motif</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Raison de l'assignation..." rows={2} data-testid="assign-reason" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAssignOpen(false)} className="flex-1">Annuler</Button>
              <Button type="submit" className="flex-1" data-testid="assign-submit-btn">Assigner</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
